import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_LOCALE,
  errorMessage,
  messagesFor,
  shopWordsFor,
  withDukaanWords,
  type Locale,
  type Messages,
  type ShopWords,
} from "@pharmacy/i18n";

import { ApiError } from "@/api/errors";
import { IS_DUKAANBOOK } from "@/brand";
import { readSetting, writeSetting } from "@/utils/storage";

/**
 * Words, bound to React (a copy of apps/mobile/src/i18n/index.tsx).
 *
 * The catalogue is a typed object in @pharmacy/i18n: `t.signIn` is a property
 * the compiler checks, so a typo is a build error, not a page showing its key.
 * Somali is the default; the choice is kept in localStorage `fb.locale`.
 */

interface I18nValue {
  t: Messages;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /**
   * A caught error, for a person. Every page that calls the server needs this:
   * the server's English message is only a fallback, never what a pharmacist
   * reads when there is a translation for the code.
   */
  errorText: (e: unknown) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

/** The saved language, or Somali. */
export function savedLocale(): Locale {
  const saved = readSetting("fb.locale");
  return saved === "en" || saved === "so" ? saved : DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(savedLocale);
  // DukaanBook: the shared words with the shop's on top ("dukaan", "alaab").
  const t = useMemo(
    () => (IS_DUKAANBOOK ? withDukaanWords(messagesFor(locale), locale) : messagesFor(locale)),
    [locale],
  );

  // The page's language, for screen readers and the browser's own spelling.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    writeSetting("fb.locale", next);
    setLocaleState(next);
  }, []);

  const errorText = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiError) return errorMessage(t, e.code, e.serverMessage, e.amount);
      // Not from the server: a bug of ours, or an answer that failed its
      // contract. Something plain rather than a stack trace.
      return t.errors.unknown;
    },
    [t],
  );

  const value = useMemo(() => ({ t, locale, setLocale, errorText }), [t, locale, setLocale, errorText]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside an I18nProvider");
  return value;
}

/** The common case: `const t = useT(); <p>{t.signIn}</p>` */
export function useT(): Messages {
  return useI18n().t;
}

/** Words of DukaanBook's own pages (its products and units). */
export function useShopT(): ShopWords {
  const { locale } = useI18n();
  return useMemo(() => shopWordsFor(locale), [locale]);
}
